import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Mail, Trash2, Plus, RefreshCw, ExternalLink, Eye, Check, Download, Send } from "lucide-react";
import ReactQuill from "react-quill";
import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";
import Api from "../../../../shared/api/api";
import BookstoreForm, { displayDate, localDateInput, isoDate, statusLabel, storeTypeLabel } from "../components/BookstoreForm";
import "../styles/AdminBookstores.css";

const modules = { toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]] };
const EMPTY_CONTACT = { channel: "note", direction: "internal", body_text: "", occurred_at: "" };
const defaultTemplate = (choices, type) => choices.find(item => item.template_name === `${type || "bookstore"}_outreach`)?.template_name || choices[0]?.template_name || "";

export default function AdminBookstoreDetail() {
  const { id } = useParams(), navigate = useNavigate();
  const [store, setStore] = useState(null), [books, setBooks] = useState([]), [contacts, setContacts] = useState([]), [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true), [error, setError] = useState(""), [notice, setNotice] = useState("");
  const [tab, setTab] = useState("details"), [saving, setSaving] = useState(false), [dirty, setDirty] = useState(false);
  const [template, setTemplate] = useState(""), [draft, setDraft] = useState(null), [draftLoading, setDraftLoading] = useState(false);
  const [draftDirty, setDraftDirty] = useState(false), [followUp, setFollowUp] = useState("");
  const [contact, setContact] = useState(EMPTY_CONTACT), [preview, setPreview] = useState(false);
  const [outlookDraft, setOutlookDraft] = useState(null);
  const [azureEnabled, setAzureEnabled] = useState(false), [azureAttempt, setAzureAttempt] = useState(false);
  const sending = useRef(false);
  const request = useRef(null), mounted = useRef(true), draftSequence = useRef(0);

  const load = useCallback(async () => {
    const [detail, catalog] = await Promise.all([Api.getBookstore(id), Api.getBookstoreBooks()]);
    const choices = await Api.getBookstoreTemplates(detail.bookstore.store_type || "bookstore");
    return { detail, catalog, choices };
  }, [id]);

  useEffect(() => {
    mounted.current = true;
    let active = true;
    setLoading(true); setError(""); setStore(null); setDraft(null); setTemplate("");
    setDirty(false); setDraftDirty(false); setOutlookDraft(null); setAzureAttempt(false); setAzureEnabled(false); request.current = null;
    load().then(({ detail, catalog, choices }) => {
      if (!active) return;
      setStore(detail.bookstore); setContacts(detail.contacts); setBooks(catalog.books); setTemplates(choices.templates);
      setAzureEnabled(detail.email_delivery?.azure_enabled === true);
      setFollowUp(localDateInput(detail.bookstore.follow_up_at));
      setTemplate(defaultTemplate(choices.templates, detail.bookstore.store_type));
    }).catch(err => { if (active) setError(err.message); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; mounted.current = false; draftSequence.current += 1; };
  }, [load]);

  const refreshHistory = async () => {
    const detail = await Api.getBookstore(id);
    if (!mounted.current) return;
    setStore(detail.bookstore); setContacts(detail.contacts); setDirty(false);
    setFollowUp(localDateInput(detail.bookstore.follow_up_at));
  };
  const save = async data => {
    setSaving(true); setError(""); setNotice("");
    try {
      const result = await Api.updateBookstore(id, data);
      draftSequence.current += 1; setDraftLoading(false); setTemplates([]); setTemplate("");
      setStore(result.bookstore); setDirty(false); setDraft(null); setDraftDirty(false); request.current = null;
      setOutlookDraft(null); setAzureAttempt(false);
      setFollowUp(localDateInput(result.bookstore.follow_up_at)); setNotice("Outreach details saved.");
      const choices = await Api.getBookstoreTemplates(result.bookstore.store_type || "bookstore");
      setTemplates(choices.templates); setTemplate(defaultTemplate(choices.templates, result.bookstore.store_type));
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${store.store_name} and its contact history?`)) return;
    setSaving(true); setError("");
    try { await Api.deleteBookstore(id); navigate("/admin/bookstores"); }
    catch (err) { setError(err.message); setSaving(false); }
  };
  const loadDraft = async () => {
    if (dirty) { setError("Save bookstore changes before loading an email draft."); return; }
    if (request.current && !window.confirm("Load a new draft? Verify delivery of the previous attempt before sending another email.")) return;
    if (draftDirty && !window.confirm("Replace the current email draft with this template?")) return;
    const sequence = ++draftSequence.current;
    setDraftLoading(true); setError(""); setNotice("");
    try {
      const result = await Api.previewBookstoreEmail(id, template);
      if (sequence !== draftSequence.current) return;
      setDraft(result); setDraftDirty(false); setOutlookDraft(null); setAzureAttempt(false); request.current = null;
    } catch (err) { if (sequence === draftSequence.current) setError(err.message); }
    finally { if (sequence === draftSequence.current) setDraftLoading(false); }
  };
  const changeDraft = (key, value) => {
    setOutlookDraft(null);
    setDraft(current => ({ ...current, [key]: value })); setDraftDirty(true);
  };
  const newAttempt = () => {
    if (!window.confirm("Start a new email attempt? Verify that the previous attempt was not delivered before sending again.")) return;
    request.current = null; setOutlookDraft(null); setAzureAttempt(false); setError(""); setNotice("");
  };
  const logContact = async event => {
    event.preventDefault(); setSaving(true); setError(""); setNotice("");
    try {
      await Api.addBookstoreContact(id, { ...contact, occurred_at: isoDate(contact.occurred_at) });
      await refreshHistory(); setContact(EMPTY_CONTACT); setNotice("Contact recorded.");
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const downloadOutlook = async () => {
    if (saving || sending.current || dirty || request.current || !draft?.recipient) return;
    setSaving(true); setError(""); setNotice("");
    try {
      const result = await Api.getBookstoreOutlookDraft(id, draft);
      const bytes = Uint8Array.from(window.atob(result.content_base64), char => char.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "message/rfc822" }));
      const anchor = document.createElement("a");
      const today = new Date();
      const date = `${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}${today.getFullYear()}`;
      const bookstore = (store.store_name || "bookstore").replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_").trim().replace(/[. ]+$/, "").slice(0, 150) || "bookstore";
      const emailType = draft.template_name?.endsWith("_follow_up") ? "follow-up" : "outreach";
      anchor.href = url; anchor.download = `${bookstore}_${emailType}_${date}.eml`;
      document.body.appendChild(anchor);
      try { anchor.click(); } finally { anchor.remove(); window.setTimeout(() => URL.revokeObjectURL(url), 1000); }
      setOutlookDraft({ ...draft }); request.current = true;
      setNotice("Outlook draft download requested. Open the file in Outlook and confirm the From account is author@kennethlpowell.com before sending. Nothing has been sent by the website.");
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };
  const sendAzure = async () => {
    if (!azureEnabled || saving || sending.current || dirty || request.current || !draft?.recipient) return;
    if (!window.confirm(`Send this email from author@kennethlpowell.com to ${draft.recipient}?`)) return;
    const requestId = window.crypto.randomUUID();
    request.current = requestId; sending.current = true; setAzureAttempt(true);
    setSaving(true); setError(""); setNotice("");
    try {
      const result = await Api.sendBookstoreEmail(id, { ...draft, request_id: requestId, follow_up_at: isoDate(followUp) });
      if (!mounted.current) return;
      if (result.status === "sent") {
        navigate("/admin/bookstores", { state: { notice: result.message || "Azure accepted the email. Recipient delivery is not yet confirmed." } });
      } else {
        setNotice(result.message || "The email attempt is pending. Verify delivery before starting another attempt.");
      }
    } catch (err) {
      if (mounted.current) setError(`${err.message} Verify this attempt's delivery before starting another; it has not been retried.`);
    } finally {
      sending.current = false;
      if (mounted.current) setSaving(false);
    }
  };
  const recordOutlookSend = async () => {
    if (!outlookDraft || saving) return;
    if (!window.confirm("Confirm you sent this draft in Outlook from author@kennethlpowell.com to this contact. This records your confirmation, not a provider delivery receipt.")) return;
    setSaving(true); setError("");
    try {
      const plain = new DOMParser().parseFromString(DOMPurify.sanitize(outlookDraft.body_html), "text/html").body.textContent || "";
      await Api.addBookstoreContact(id, { channel: "email", direction: "outgoing", follow_up_at: isoDate(followUp), body_text: `Manually confirmed Outlook send from author@kennethlpowell.com\nTo: ${outlookDraft.recipient}\nSubject: ${outlookDraft.subject}\n\n${plain.slice(0, 19000)}` });
      navigate("/admin/bookstores", { state: { notice: "Outlook send recorded from your confirmation." } });
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="bookstore-page" role="status">Loading bookstore...</div>;
  if (!store) return <div className="bookstore-page"><Link to="/admin/bookstores">Back to bookstores</Link><p role="alert" className="bookstore-error">{error || "Bookstore not found."}</p></div>;

  return <div className="bookstore-page">
    <Link className="bookstore-back" to="/admin/bookstores"><ArrowLeft size={17} />Bookstores</Link>
    <header className="bookstore-header"><div><h1>{store.store_name}</h1><p className="bookstore-muted">{[store.city, store.state].filter(Boolean).join(", ") || store.contact_name || ""}</p></div>
      <div className="bookstore-actions"><span className="bookstore-status">{storeTypeLabel(store.store_type)}</span><span className={`bookstore-status status-${store.status}`}>{statusLabel(store.status)}</span>
        {store.website && <a href={store.website} target="_blank" rel="noreferrer" title="Open bookstore website" aria-label="Open bookstore website" className="bookstore-icon"><ExternalLink size={18} /></a>}
        <button title="Delete bookstore" aria-label="Delete bookstore" disabled={saving} onClick={remove}><Trash2 size={18} /></button></div>
    </header>
    <div className="bookstore-contact-strip"><span>{store.email || "No email address"}</span><span>Last contact: {displayDate(store.last_contacted_at || store.contacted_at)}</span><span>Follow-up: {displayDate(store.follow_up_at)}</span></div>
    <div className="bookstore-tabs" role="tablist" aria-label="Bookstore sections">
      {[['details', 'Details'], ['email', 'Email'], ['history', 'Contact history']].map(([key, label]) => <button key={key} id={`bookstore-tab-${key}`} role="tab" aria-selected={tab === key} aria-controls={`bookstore-panel-${key}`} disabled={saving} onClick={() => { setTab(key); setError(""); }}>{key === "email" && <Mail size={16} />}{label}</button>)}
    </div>
    {error && <p role="alert" className="bookstore-error">{error}</p>}
    {notice && <p role="status" className="bookstore-notice">{notice}</p>}
    <section hidden={tab !== "details"} role="tabpanel" id="bookstore-panel-details" aria-labelledby="bookstore-tab-details">
      <BookstoreForm bookstore={store} books={books} onSave={save} saving={saving} onDirty={setDirty} submitLabel={store.store_type === "library" ? "Save library" : "Save bookstore"} />
    </section>
    <section hidden={tab !== "email"} role="tabpanel" id="bookstore-panel-email" aria-labelledby="bookstore-tab-email">
      <div className="bookstore-template-row">
        <label>Email template<select value={template} disabled={saving || draftLoading} onChange={event => setTemplate(event.target.value)}>
          {!templates.length && <option value="">No active outreach templates</option>}
          {templates.map(item => <option key={item.template_name} value={item.template_name}>{item.template_name}</option>)}
        </select></label>
        <button onClick={loadDraft} disabled={!template || draftLoading || saving || dirty}><RefreshCw size={16} />{draftLoading ? "Loading..." : "Load template"}</button>
        <Link to="/admin/email-templates">Manage templates</Link>
        <Link to="/admin/bookstores/pricing">Book pricing</Link>
      </div>
      {dirty && <p role="status" className="bookstore-error">Save bookstore changes before composing an email.</p>}
      {draft && <form onSubmit={event => event.preventDefault()} className="bookstore-email-form">
        <div className="bookstore-fields">
          <label>To<input type="email" readOnly value={draft.recipient} /></label>
          <label>{azureEnabled ? "From" : "Outlook From"}<input type="email" readOnly value="author@kennethlpowell.com" /></label>
          <label>Follow-up date<input type="datetime-local" disabled={saving || Boolean(outlookDraft) || azureAttempt} value={followUp} onChange={event => setFollowUp(event.target.value)} /></label>
        </div>
        <label>Subject<input required maxLength={255} value={draft.subject} disabled={saving || Boolean(outlookDraft) || azureAttempt} onChange={event => changeDraft("subject", event.target.value)} /></label>
        <label id="bookstore-message-label">Message</label>
        <div aria-labelledby="bookstore-message-label"><ReactQuill theme="snow" value={draft.body_html} onChange={value => changeDraft("body_html", value)} modules={modules} readOnly={saving || Boolean(outlookDraft) || azureAttempt} /></div>
        <div className="bookstore-actions">
          <button type="button" onClick={() => setPreview(!preview)}><Eye size={17} />{preview ? "Close preview" : "Preview"}</button>
          {azureEnabled && <button type="button" onClick={sendAzure} className="bookstore-primary" disabled={saving || dirty || Boolean(outlookDraft) || azureAttempt || !draft.recipient}><Send size={17} />Send email</button>}
          <button type="button" onClick={downloadOutlook} className={azureEnabled ? "" : "bookstore-primary"} disabled={saving || dirty || Boolean(outlookDraft) || azureAttempt || !draft.recipient}><Download size={17} />Download Outlook draft</button>
          {outlookDraft && <button type="button" onClick={recordOutlookSend} disabled={saving || dirty}><Check size={17} />Record Outlook send</button>}
          {(outlookDraft || azureAttempt) && <button type="button" onClick={newAttempt} disabled={saving}><Plus size={17} />New attempt</button>}
        </div>
        {preview && <iframe className="bookstore-email-preview" title="Bookstore email preview" sandbox="" srcDoc={DOMPurify.sanitize(draft.body_html)} />}
      </form>}
    </section>
    <section hidden={tab !== "history"} role="tabpanel" id="bookstore-panel-history" aria-labelledby="bookstore-tab-history">
      <form className="bookstore-log-form" onSubmit={logContact}><h2>Record contact</h2>
        <div className="bookstore-fields">
          <label>Channel<select value={contact.channel} onChange={event => setContact({ ...contact, channel: event.target.value })}>{["note", "email", "phone", "meeting"].map(value => <option key={value} value={value}>{value}</option>)}</select></label>
          <label>Direction<select value={contact.direction} onChange={event => setContact({ ...contact, direction: event.target.value })}>{["internal", "outgoing", "incoming"].map(value => <option key={value} value={value}>{value}</option>)}</select></label>
          <label>Contact date<input type="datetime-local" value={contact.occurred_at} onChange={event => setContact({ ...contact, occurred_at: event.target.value })} /></label>
        </div>
        <label>Contact notes<textarea required maxLength={20000} rows={3} value={contact.body_text} onChange={event => setContact({ ...contact, body_text: event.target.value })} /></label>
        <div className="bookstore-actions"><button type="submit" className="bookstore-primary" disabled={saving || dirty}><Plus size={17} />{saving ? "Saving..." : "Record contact"}</button></div>
        {dirty && <p className="bookstore-error">Save bookstore changes before recording a contact.</p>}
      </form>
      <div className="bookstore-history-heading"><h2>Contact history</h2><button title="Refresh contact history" aria-label="Refresh contact history" disabled={saving || dirty} onClick={() => refreshHistory().catch(err => setError(err.message))}><RefreshCw size={17} /></button></div>
      {!contacts.length ? <p className="bookstore-muted">No contact history yet.</p> : <ol className="bookstore-history">{contacts.map(entry => <li key={entry.id}>
        <header><strong>{entry.subject || statusLabel(entry.channel)}</strong><span className={`bookstore-status status-${entry.status}`}>{entry.status}</span></header>
        <p className="bookstore-muted">{displayDate(entry.occurred_at)} · {entry.direction} {entry.channel}{entry.recipient ? ` · ${entry.recipient}` : ""}</p>
        {entry.error_message && <p className="bookstore-error">{entry.error_message}</p>}
        {entry.status === "sending" && <p className="bookstore-error">Delivery is not yet recorded. Verify the original attempt before sending again.</p>}
        <details><summary>{entry.channel === "email" ? "Email content" : "Contact notes"}</summary><pre>{entry.body_text || ""}</pre></details>
      </li>)}</ol>}
    </section>
  </div>;
}
